import config from "../config/env";
import { User } from "../model/userModels";
import { setAuthTokenCookie } from "./AuthService";
import { compressImage } from "../utils/imageUtils";
import { fetchWithRetry } from "../utils/fetchUtils";
import { FileData } from "../utils/fileUtils";

type CreateCareScreenerSubmissionRequest = {
  typeformResponseId: string;
  typeformId: string;
  typeformPath: string;
};

type CreateCareScreenerSubmissionResponse = {
  id: number;
};

type CheckCareScreenerInsuranceEligibilityRequest = {
  careScreenerSubmissionId: number;
  typeformId: string;
  typeformResponseId: string;
  cptCode?: string;
};

type CreateCareScreenerAccounts = {
  careScreenerSubmissionId: number;
  typeformId: string;
  typeformResponseId: string;
  password: string;
  timeZone: string;
  timeZoneOffset: string;
  enableFullDti?: boolean;
};

type SubmitInsuranceCardRequest = {
  id: number;
  frontImage: object;
  backImage: object | null;
};

type SubmitInsuranceCardManualRequest = {
  id: number;
  memberId: string;
  groupNumber: string;
};

type SubmitInsuranceCardResponse = {
  eligibilityStatus: InsuranceEligibilityStatus;
  payerName?: string;
};

export type InsuranceEligibilityStatus = "ELIGIBLE" | "INELIGIBLE" | "NOT_APPLICABLE" | "UNKNOWN";
export type DirectIntakeStatus = "UNQUALIFIED" | "QUALIFIED" | "INTAKE_REQUESTED" | "NOT_APPLICABLE" | "SCHEDULABLE";

export type InsuranceEligibilityCheckResponse = {
  careScreenerSubmissionId: number;
  eligibilityStatus: InsuranceEligibilityStatus;
  insuranceEstimate?: InsuranceEstimate;
};

export type InsuranceEstimate = {
  payerId: string;
  planName: string;
  planStatus: "ACTIVE" | "INACTIVE" | "ACTIVE_AT_RISK";
  mentalHealthCoverage: "UNKNOWN" | "ACTIVE" | "INACTIVE";
  copaymentInCents: number | undefined;
  coinsurancePercent: number | undefined;
  deductibleInCents: number | undefined;
  remainingDeductibleInCents: number | undefined;
  oopMaxInCents: number | undefined;
  remainingOopMaxInCents: number | undefined;
  memberObligationInCents: number | undefined;
  postDeductibleMemberObligationInCents: number | undefined;
  postOopMaxMemberObligationInCents: number | undefined;
};

export type Candidates = Candidate[];

export type Candidate = {
  user: User;
  insurancePlanName: string;
  insuranceEligibilityStatus: InsuranceEligibilityStatus;
};

export type CompleteCareScreenerResponse = {
  userId: number;
  careTeamFollowUp?: boolean;
  directIntakeStatus: DirectIntakeStatus | undefined;
  careCoordinator?: User;
  jobId: string | undefined;
  requestDirectIntakeJobId: string | undefined;
  token: string | null;
  challengeToken: string | null;
  state: string;
  candidates: Candidates;
  schedulingQueryStartDate?: string;
};

export type DiscoveryResult = {
  found: boolean;
  payerName?: string;
  skipInsurance?: boolean;
};

export type EligibilityEstimate = {
  sessionCostInCents?: number;
  memberObligationInCents?: number;
  postDeductibleMemberObligationInCents?: number;
  postOopMaxMemberObligationInCents?: number;
  copaymentInCents?: number;
  coinsurancePercent?: number;
  deductibleInCents?: number;
  remainingDeductibleInCents?: number;
  outOfPocketInCents?: number;
  remainingOutOfPocketInCents?: number;
  planStatus?: string;
  mentalHealthCoverage?: string;
};

export type EligibilityResult = {
  status: "ELIGIBLE" | "INELIGIBLE" | "UNKNOWN" | "PENDING";
  estimate?: EligibilityEstimate;
};

export type RequestSmsVerificationCodeResponse = {
  success: boolean;
  phone: string;
  message?: string;
};

export type VerifyTOTPRequest = {
  totp: string;
};

export type VerifyTOTPResponse = {
  success: boolean;
  id?: number;
  token?: string;
  twoFAtoken?: string;
  firstName?: string;
  accountType?: string;
  familyId?: number;
  status?: string;
  roles?: string[];
  providerName?: string;
  authyStatus?: string;
  message?: string;
};

const CareScreenerService = {
  async createCareScreenerSubmission({
    typeformId,
    typeformResponseId,
    typeformPath
  }: CreateCareScreenerSubmissionRequest): Promise<CreateCareScreenerSubmissionResponse> {
    const response = await fetch(`${config.env.REACT_APP_API}/careScreenerSubmission${window.location.search}`, {
      body: JSON.stringify({ typeformResponseId, typeformId, typeformPath }),
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });
    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to create care screener submission");
    }
    return await response.json();
  },
  async checkCareScreenerInsuranceEligibility({
    typeformId,
    typeformResponseId,
    careScreenerSubmissionId: id,
    cptCode
  }: CheckCareScreenerInsuranceEligibilityRequest): Promise<InsuranceEligibilityCheckResponse[]> {
    const response = await fetch(
      `${config.env.REACT_APP_API}/careScreenerSubmissions/${id}/actions/checkInsuranceEligibility`,
      {
        body: JSON.stringify({ typeformResponseId, typeformId, cptCode }),
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      }
    );
    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to check care screener insurance eligibility");
    }
    return await response.json();
  },
  async completeCareScreener(request: CreateCareScreenerAccounts): Promise<CompleteCareScreenerResponse> {
    const response = await fetch(`${config.env.REACT_APP_API}/completeCareScreener`, {
      body: JSON.stringify(request),
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to complete care screener");
    }

    const result = await response.json();

    if (result.challengeToken) {
      sessionStorage.setItem("challengeToken", result.challengeToken);
    }

    if (result.token) {
      setAuthTokenCookie(result.token, { expiresInMillis: 24 * 60 * 60 * 1000 });
      sessionStorage.setItem("role", "PARENT");
      sessionStorage.setItem("userId", result.userId);
    }

    return result;
  },

  async requestSmsVerificationCode(): Promise<RequestSmsVerificationCodeResponse> {
    const challengeToken = sessionStorage.getItem("challengeToken");

    if (!challengeToken) {
      throw new Error("Please try logging in again.");
    }

    const response = await fetch(`${config.env.REACT_APP_API}/requestSmsVerificationCode`, {
      body: JSON.stringify({ challengeToken }),
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to request SMS verification code");
    }

    const result = await response.json();

    if (result.challengeToken) {
      sessionStorage.setItem("challengeToken", result.challengeToken);
    }

    return result;
  },

  async submitInsuranceCard({ id, frontImage, backImage }: SubmitInsuranceCardRequest): Promise<SubmitInsuranceCardResponse> {
    const [compressedFront, compressedBack] = await Promise.all([
      compressImage(frontImage as FileData),
      backImage ? compressImage(backImage as FileData) : Promise.resolve(null)
    ]);
    const response = await fetchWithRetry(
      `${config.env.REACT_APP_API}/careScreenerSubmissions/${id}/insuranceCard`,
      {
        body: JSON.stringify({ frontImage: compressedFront, backImage: compressedBack }),
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      }
    );
    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to submit insurance card");
    }
    return await response.json();
  },

  async submitInsuranceCardManual({ id, memberId, groupNumber }: SubmitInsuranceCardManualRequest): Promise<SubmitInsuranceCardResponse> {
    const response = await fetchWithRetry(
      `${config.env.REACT_APP_API}/careScreenerSubmissions/${id}/insuranceCard`,
      {
        body: JSON.stringify({ memberId, groupNumber }),
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      }
    );
    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to submit insurance card");
    }
    return await response.json();
  },

  async runCareScreenerDiscovery(careScreenerSubmissionId: number): Promise<DiscoveryResult> {
    const response = await fetch(
      `${config.env.REACT_APP_API}/careScreenerSubmissions/${careScreenerSubmissionId}/actions/runDiscovery`,
      {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" }
      }
    );
    if (!response.ok) throw new Error("Insurance discovery failed");
    return await response.json();
  },

  async getCareScreenerEligibilityResult(careScreenerSubmissionId: number): Promise<EligibilityResult> {
    const response = await fetch(
      `${config.env.REACT_APP_API}/careScreenerSubmissions/${careScreenerSubmissionId}/actions/eligibilityResult`,
      {
        method: "GET",
        headers: { Accept: "application/json" }
      }
    );
    if (!response.ok) throw new Error("Failed to fetch eligibility result");
    return await response.json();
  },

  async verifyTOTP({ totp }: VerifyTOTPRequest): Promise<VerifyTOTPResponse> {
    const challengeToken = sessionStorage.getItem("challengeToken");

    const requestBody = { challengeToken, totp };

    const response = await fetch(`${config.env.REACT_APP_API}/verifyTOTP`, {
      body: JSON.stringify(requestBody),
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || "Failed to verify TOTP code");
    }

    const result = await response.json();

    if (result.token) {
      setAuthTokenCookie(result.token, { expiresInMillis: 24 * 60 * 60 * 1000 });
      sessionStorage.setItem("role", result.accountType || "PARENT");
      sessionStorage.setItem("userId", result.id?.toString() || "");
      sessionStorage.setItem("user", result.firstName || "");
      sessionStorage.setItem("familyId", result.familyId?.toString() || "");

      sessionStorage.removeItem("challengeToken");
    }

    return result;
  }
};

export default CareScreenerService;
